import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../components/layout/Header";
import { SharedButton, SharedCard } from "../components/shared";
import { setJourneyMode } from "../js/state/sellerState";
import ReMark from "../components/brand/ReMark";
import Headline from "../components/brand/Headline";

const HERO_SHOTS = [
  "/images/home/hero-house-left.jpg",
  "/images/home/hero-hq.jpg",
  "/images/home/hero-house-right.jpg",
];

function useHeroReady(sources) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loaded = 0;
    const mark = () => {
      loaded += 1;
      if (!cancelled && loaded >= sources.length) setReady(true);
    };

    sources.forEach((src) => {
      const img = new Image();
      img.onload = mark;
      img.onerror = mark;
      img.src = src;
    });

    return () => {
      cancelled = true;
    };
  }, [sources]);

  return ready;
}

export default function StartScreen() {
  const heroReady = useHeroReady(HERO_SHOTS);

  return (
    <AppLayout variant="public">
      <div className="landing-frame" data-tour="re-home">
        <section className={`landing-hero${heroReady ? " is-ready" : ""}`} data-tour="re-hero">
          <div className="landing-hero__mosaic" aria-hidden="true">
            <img
              className="landing-hero__shot landing-hero__shot--left"
              src="/images/home/hero-house-left.jpg"
              alt=""
              width="900"
              height="675"
              decoding="async"
              loading="eager"
              fetchPriority="high"
            />
            <img
              className="landing-hero__shot landing-hero__shot--center"
              src="/images/home/hero-hq.jpg"
              alt=""
              width="1400"
              height="788"
              decoding="async"
              loading="eager"
              fetchPriority="high"
            />
            <img
              className="landing-hero__shot landing-hero__shot--right"
              src="/images/home/hero-house-right.jpg"
              alt=""
              width="800"
              height="1067"
              decoding="async"
              loading="eager"
              fetchPriority="high"
            />
            <div className="landing-hero__lens" />
          </div>
          <p className="landing-kicker">
            <ReMark /> · DreamHome Journey
          </p>
          <div className="landing-hero__inner">
            <div className="landing-hero__stage">
              <div className="landing-hero__focus-lens" aria-hidden="true" />
              <Headline as="h1" className="landing-hero__title">
                Welcome, buyers and sellers.
              </Headline>
              <p className="journey-tagline landing-hero__path">
                A calm, clear path that leads to the home you love —
                <br />
                or the home you&apos;re ready to offer
              </p>
              <p className="landing-hero__here">
                <ReMark /> is here for you
              </p>
            </div>
          </div>

          <div className="landing-hero__lower">
            <div className="landing-bridge">
              <p>
                Name what matters
                <span className="landing-bridge__sep" aria-hidden="true">
                  {" "}
                  •{" "}
                </span>
                keep it organized
                <span className="landing-bridge__sep" aria-hidden="true">
                  {" "}
                  •{" "}
                </span>
                share it with your agent
              </p>
              <p>Care, clarity, and a modern process — without the noise</p>
            </div>
            <div className="landing-ctas">
              <span data-tour="re-signin" className="landing-ctas__item">
                <SharedButton to="/login" variant="primary">
                  Sign in
                </SharedButton>
              </span>
              <span data-tour="re-buyer" className="landing-ctas__item">
                <SharedButton to="/start" variant="secondary" onClick={() => setJourneyMode("buyer")}>
                  Buyer Journey
                </SharedButton>
              </span>
              <span data-tour="re-seller" className="landing-ctas__item">
                <SharedButton to="/seller/home" variant="secondary" onClick={() => setJourneyMode("seller")}>
                  Seller Journey
                </SharedButton>
              </span>
              <span data-tour="re-dash" className="landing-ctas__item">
                <SharedButton to="/dashboard" variant="ghost">
                  Main Dashboard
                </SharedButton>
              </span>
            </div>
          </div>

          <section className="landing-purpose" data-tour="re-purpose">
            <div className="title-rule" />
            <h2>
              <ReMark />
            </h2>
            <div className="title-rule" />
            <ul className="landing-purpose__points">
              <li>Checklist Precision = Clear Paths</li>
              <li>Centralized Communications</li>
              <li>Organization = Time Saving</li>
            </ul>
          </section>
        </section>
      </div>

      <div className="journey-mode-grid">
        <SharedCard className="journey-mode-card journey-mode-card--buyer journey-mode-card--photo-right" eyebrow="Buyers">
          <div className="journey-mode-card__copy">
            <Headline as="h2" className="shared-card-title">
              Envision the home you truly want
            </Headline>
            <p>
              Shape your future with clarity — define your ideal location, home type, lot features,
              interior style, lifestyle needs, accessibility preferences, and personal notes. Complete
              your journey with a streamlined transaction checklist, then send your vision directly to
              your agent with confidence.
            </p>
            <SharedButton to="/start" variant="primary" onClick={() => setJourneyMode("buyer")}>
              Enter Buyer Journey
            </SharedButton>
          </div>
          <img
            className="journey-mode-card__photo"
            src="/images/home/modern-suburb.png"
            alt="A modern suburban house with warm cedar, white walls, and a quiet lawn."
          />
        </SharedCard>
        <SharedCard className="journey-mode-card journey-mode-card--seller journey-mode-card--photo-right" eyebrow="Sellers">
          <div className="journey-mode-card__copy">
            <Headline as="h2" className="shared-card-title">
              Present your property at its best
            </Headline>
            <p>
              Share a clear, honest profile of your home — features, condition, photos, and
              preparation tips that elevate buyer interest and empower your agent to deliver
              exceptional results.
            </p>
            <SharedButton to="/seller/home" variant="primary" onClick={() => setJourneyMode("seller")}>
              Enter Seller Journey
            </SharedButton>
          </div>
          <img
            className="journey-mode-card__photo"
            src="/images/home/urban-neighborhood.png"
            alt="An urban neighborhood house on a tree-lined city street."
          />
        </SharedCard>
        <SharedCard className="journey-mode-card journey-mode-card--quiet journey-mode-card--photo-left" eyebrow="Your agent">
          <div className="journey-mode-card__copy">
            <Headline as="h2" className="shared-card-title">
              Your agent, supported by modern clarity
            </Headline>
            <p>
              Behind the scenes, your agent works within a streamlined, organized workspace that
              keeps your details, appointments, and progress perfectly aligned. <ReMark /> helps them
              stay focused on you — not the scramble — so your experience feels smooth, attentive, and
              confidently guided.
            </p>
          </div>
          <img
            className="journey-mode-card__photo"
            src="/images/home/log-house.jpg"
            alt="A large woodland log house among trees, with warm lights glowing in the windows."
          />
        </SharedCard>
        <SharedCard className="journey-mode-card journey-mode-card--quiet journey-mode-card--photo-left" eyebrow="Your office">
          <div className="journey-mode-card__copy">
            <Headline as="h2" className="shared-card-title">
              An office that operates with quiet precision
            </Headline>
            <p>
              Your real estate office uses <ReMark /> to maintain consistency, compliance, and quality
              across every client journey. Leadership gains the clarity to support agents effectively,
              ensuring your experience benefits from a modern, well-coordinated team working on your
              behalf.
            </p>
          </div>
          <img
            className="journey-mode-card__photo"
            src="/images/home/stone-house.png"
            alt="A traditional stone house among mature oaks."
          />
        </SharedCard>
      </div>
      <p className="muted" style={{ textAlign: "center", marginTop: "1.25rem" }}>
        Looking around is welcome. <Link to="/login">Sign in</Link> when you are ready —
        registration never comes first. Professionals use Professional Login in the menu.
      </p>
    </AppLayout>
  );
}
